import React, { ReactElement, useEffect, useState } from "react";
import {
    Container,
    Item,
    ItemView,
    OrganizationName,
    Icon,
    OrganizationId,
    PageContent,
    Header,
    Title,
    SignoutLink,
    ItemInfoView,
    ItemViewGroup,
    Input,
    InputGroup
} from "./styles";
import { Organization } from "../../services/models/Organization";
import { useNavigate } from "react-router-dom";
import { GenericAlert } from "../../components/alert/GenericAlert";
import { OrganizationService } from "../../services/organization-service/OrganizationService";
import AuthService from "../../services/auth-service/AuthService";
import Storage from "../../services/storage-service/Storage";
import Shards from "../../components/Shards";
import DocumentationService from "../../services/documentation-service/DocumentationService";

const SelectOrganizationView = (): ReactElement => {
    const initialStatusAlert = {
        theme: "",
        message: "",
        isOpen: false
    };
    const [statusAlert, setStatusAlert] = useState(initialStatusAlert);
    const [isFetching, setIsFetching] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [search, setSearch] = useState("");

    const filteredOrganizations =
        search.length > 0
            ? organizations.filter(org =>
                  org.name.toLowerCase().includes(search.toLowerCase())
              )
            : [];
    const navigate = useNavigate();

    useEffect(() => {
        getOrganizations();
    }, []);

    const onChangeInput = (value: string) => {
        setSearch(value);
    };

    const getOrganizations = async () => {
        try {
            setIsFetching(true);
            const resultOrganizations = await OrganizationService.getUserOrganizations();

            setOrganizations(resultOrganizations);
            Storage.set(
                Storage.KEYS.ORGANIZATIONS,
                JSON.stringify(resultOrganizations)
            );

            if (resultOrganizations?.length === 1) {
                onClick(resultOrganizations?.[0]);
                return;
            }

            setIsFetching(false);
        } catch (error) {
            console.error(error);
            setStatusAlert({
                message:
                    "Houve um erro inesperado ao listar suas organizações. Por favor, tente novamente.",
                theme: "danger",
                isOpen: true
            });
            setIsFetching(false);
        }
    };

    const onClick = (organization: Organization) => {
        Storage.set(
            Storage.KEYS.ORGANIZATION_DATA,
            JSON.stringify(organization)
        );
        navigateToInitialPage(organization);
    };

    const closeAlert = () => {
        setStatusAlert(initialStatusAlert);
    };

    const navigateToInitialPage = async (
        organization: Organization
    ): Promise<void> => {
        const userProfile = `${
            AuthService.getDecodedToken().role
        }`?.toLowerCase();
        const organizationName: string = organization?.name?.toLowerCase();
        const fileName: string = `${organizationName}_${userProfile}`;
        const hasDoc: any = await DocumentationService.getDocumentation(
            fileName
        );
        if (hasDoc) {
            return navigate("/getting-started");
        }

        navigate("/applications/1");
    };

    return (
        <Container>
            <GenericAlert
                className="mb-3 cypreGenericAlert"
                dismissible={() => closeAlert()}
                open={statusAlert.isOpen}
                theme={statusAlert.theme}
            >
                {statusAlert.message}
            </GenericAlert>
            <Header>
                <Title>Portal de eitri-apps</Title>
                <SignoutLink to="/logout">
                    <Icon style={{ fontSize: 21 }} className="material-icons">
                        logout
                    </Icon>
                    Sair
                </SignoutLink>
            </Header>
            {isFetching ? (
                <div className="loading--container">
                    <div className="lds-dual-ring"></div>
                    <h1 className="loading--text">Carregando...</h1>
                </div>
            ) : (
                <PageContent>
                    <Shards.Card>
                        <Shards.CardHeader>Organizações</Shards.CardHeader>
                        <Shards.CardBody>
                            <OrganizationSearchComponent
                                organizations={organizations}
                                onChangeInput={onChangeInput}
                            />
                            {organizations && organizations.length > 0 ? (
                                <React.Fragment>
                                    {search.length > 0
                                        ? filteredOrganizations.map(
                                              organization => (
                                                  <OrganizationItem
                                                      key={organization?.id}
                                                      organization={
                                                          organization
                                                      }
                                                      onClick={() =>
                                                          onClick(organization)
                                                      }
                                                  />
                                              )
                                          )
                                        : organizations.map(organization => (
                                              <OrganizationItem
                                                  key={organization?.id}
                                                  organization={organization}
                                                  onClick={() =>
                                                      onClick(organization)
                                                  }
                                              />
                                          ))}
                                </React.Fragment>
                            ) : (
                                <NoOrganizationComponent />
                            )}
                        </Shards.CardBody>
                    </Shards.Card>
                </PageContent>
            )}
        </Container>
    );
};

const OrganizationSearchComponent = (props: any) => {
    const { organizations = [], onChangeInput } = props;

    return (
        <React.Fragment>
            {organizations?.length ? (
                <React.Fragment>
                    <h4 style={{ marginBottom: 32 }}>
                        Selecione a organização
                    </h4>
                    <InputGroup
                        style={{
                            borderWidth: 1,
                            borderStyle: "solid"
                        }}
                    >
                        <Icon
                            style={{ fontSize: 21 }}
                            className="material-icons"
                        >
                            search
                        </Icon>
                        <Input
                            name="name"
                            placeholder="Busque a organização"
                            onChange={e => onChangeInput(e.target.value)}
                        />
                    </InputGroup>
                </React.Fragment>
            ) : (
                <React.Fragment></React.Fragment>
            )}
        </React.Fragment>
    );
};

const NoOrganizationComponent = () => {
    return <h5>Você não possui organização.</h5>;
};

const OrganizationItem = (props: any) => {
    const { organization, onClick } = props;

    return (
        <Item
            style={{
                marginBottom: 10,
                borderWidth: 1,
                borderStyle: "solid",
                borderColor: "#c9c9c9"
            }}
            key={organization.id}
            onClick={onClick}
        >
            <ItemView>
                <ItemViewGroup>
                    <Icon
                        style={{
                            fontSize: 32
                        }}
                        className="material-icons"
                    >
                        corporate_fare
                    </Icon>
                    <ItemInfoView>
                        <OrganizationName>{organization.name}</OrganizationName>
                        <OrganizationId>ID: #{organization.id}</OrganizationId>
                    </ItemInfoView>
                </ItemViewGroup>
                <Icon className="material-icons">chevron_right</Icon>
            </ItemView>
        </Item>
    );
};

export default SelectOrganizationView;
